import { supabase } from './supabaseClient';

// User notification preferences interface
export interface UserNotificationPreferences {
  id?: string;
  user_id: string;
  email_notifications_enabled: boolean;
  website_notifications_enabled: boolean;
  project_release_notifications: boolean;
  discount_offer_notifications: boolean;
  follow_up_notifications: boolean;
  blocked_projects: string[];
  blocked_publishers: string[];
  max_notifications_per_day: number;
  notification_frequency: 'immediate' | 'daily_digest' | 'weekly_digest' | 'disabled';
  preferred_notification_time: string;
  timezone: string;
  created_at?: string;
  updated_at?: string;
}

// Default preferences
const DEFAULT_PREFERENCES: Omit<UserNotificationPreferences, 'id' | 'user_id' | 'created_at' | 'updated_at'> = {
  email_notifications_enabled: true,
  website_notifications_enabled: true,
  project_release_notifications: true,
  discount_offer_notifications: true,
  follow_up_notifications: true,
  blocked_projects: [],
  blocked_publishers: [],
  max_notifications_per_day: 5,
  notification_frequency: 'immediate',
  preferred_notification_time: '09:00:00',
  timezone: 'UTC'
};

// Unsubscribe token interface
export interface UnsubscribeToken {
  token: string;
  token_type: 'project_specific' | 'all_notifications' | 'publisher_specific';
  expires_at: string;
}

export class UserNotificationPreferencesService {
  /**
   * Get user's notification preferences
   */
  static async getUserPreferences(userId: string): Promise<UserNotificationPreferences> {
    try {
      // Use the database function that returns defaults if no preferences exist
      const { data, error } = await supabase.rpc('get_user_notification_preferences', {
        user_id_param: userId
      });

      if (error) {
        console.error('Error fetching user preferences:', error);
        // Return defaults if there's an error
        return {
          user_id: userId,
          ...DEFAULT_PREFERENCES
        };
      }

      return data || {
        user_id: userId,
        ...DEFAULT_PREFERENCES
      };
    } catch (error) {
      console.error('Error in getUserPreferences:', error);
      return {
        user_id: userId,
        ...DEFAULT_PREFERENCES
      };
    }
  }

  /**
   * Update user's notification preferences
   */
  static async updateUserPreferences(
    userId: string, 
    preferences: Partial<UserNotificationPreferences>
  ): Promise<{ success: boolean; error?: any }> {
    try {
      const { error } = await supabase
        .from('user_notification_preferences')
        .upsert({
          user_id: userId,
          ...preferences,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        });

      if (error) {
        console.error('Error updating user preferences:', error);
        return { success: false, error };
      }

      return { success: true };
    } catch (error) {
      console.error('Error in updateUserPreferences:', error);
      return { success: false, error };
    }
  }

  /**
   * Check if a user can receive a specific notification
   */
  static async canUserReceiveNotification(
    userId: string,
    projectId: string,
    notificationType: 'email_template' | 'website_message' | 'discount_offer',
    publisherId?: string
  ): Promise<boolean> {
    try {
      const { data, error } = await supabase.rpc('can_user_receive_notification', {
        user_id_param: userId,
        project_id_param: projectId,
        notification_type_param: notificationType,
        publisher_id_param: publisherId || null
      });

      if (error) {
        console.error('Error checking notification permission:', error);
        // Default to allowing notifications if there's an error
        return true;
      }

      return data === true;
    } catch (error) {
      console.error('Error in canUserReceiveNotification:', error);
      // Default to allowing notifications if there's an error
      return true;
    }
  }

  /**
   * Filter users based on their notification preferences
   */
  static async filterUsersForNotification(
    userIds: string[],
    projectId: string,
    notificationType: 'email_template' | 'website_message' | 'discount_offer',
    publisherId?: string
  ): Promise<string[]> {
    const allowedUsers: string[] = [];

    // Check each user individually
    for (const userId of userIds) {
      const canReceive = await this.canUserReceiveNotification(
        userId, 
        projectId, 
        notificationType, 
        publisherId
      );
      
      if (canReceive) {
        allowedUsers.push(userId);
      }
    }

    return allowedUsers;
  }

  /**
   * Generate an unsubscribe token for a user
   */
  static async generateUnsubscribeToken(
    userId: string,
    projectId?: string,
    tokenType: 'project_specific' | 'all_notifications' | 'publisher_specific' = 'project_specific'
  ): Promise<{ token: string | null; error?: any }> {
    try {
      const { data, error } = await supabase.rpc('generate_unsubscribe_token', {
        user_id_param: userId,
        project_id_param: projectId || null,
        token_type_param: tokenType
      });

      if (error) {
        console.error('Error generating unsubscribe token:', error);
        return { token: null, error };
      }

      return { token: data };
    } catch (error) {
      console.error('Error in generateUnsubscribeToken:', error);
      return { token: null, error };
    }
  }

  /**
   * Process an unsubscribe request using a token
   */
  static async processUnsubscribeRequest(token: string): Promise<{ success: boolean; message?: string; error?: any }> {
    try {
      const { data, error } = await supabase.rpc('process_unsubscribe_request', {
        token_param: token
      });

      if (error) {
        console.error('Error processing unsubscribe request:', error);
        return { success: false, error };
      }

      return {
        success: data.success,
        message: data.message,
        error: data.error
      };
    } catch (error) {
      console.error('Error in processUnsubscribeRequest:', error);
      return { success: false, error };
    }
  }

  /**
   * Track that a notification was sent to a user
   */
  static async trackNotificationSent(
    userId: string,
    projectId: string,
    notificationType: 'email_template' | 'website_message' | 'discount_offer'
  ): Promise<{ success: boolean; error?: any }> {
    try {
      const { error } = await supabase.rpc('track_notification_sent', {
        user_id_param: userId,
        project_id_param: projectId,
        notification_type_param: notificationType
      });

      if (error) {
        console.error('Error tracking notification:', error);
        return { success: false, error };
      }

      return { success: true };
    } catch (error) {
      console.error('Error in trackNotificationSent:', error);
      return { success: false, error };
    }
  }

  /**
   * Block a specific project for a user
   */
  static async blockProject(userId: string, projectId: string): Promise<{ success: boolean; error?: any }> {
    try {
      const currentPrefs = await this.getUserPreferences(userId);
      const updatedBlockedProjects = [...currentPrefs.blocked_projects];
      
      if (!updatedBlockedProjects.includes(projectId)) {
        updatedBlockedProjects.push(projectId);
      }

      return await this.updateUserPreferences(userId, {
        blocked_projects: updatedBlockedProjects
      });
    } catch (error) {
      console.error('Error in blockProject:', error);
      return { success: false, error };
    }
  }

  /**
   * Block a specific publisher for a user
   */
  static async blockPublisher(userId: string, publisherId: string): Promise<{ success: boolean; error?: any }> {
    try {
      const currentPrefs = await this.getUserPreferences(userId);
      const updatedBlockedPublishers = [...currentPrefs.blocked_publishers];
      
      if (!updatedBlockedPublishers.includes(publisherId)) {
        updatedBlockedPublishers.push(publisherId);
      }

      return await this.updateUserPreferences(userId, {
        blocked_publishers: updatedBlockedPublishers
      });
    } catch (error) {
      console.error('Error in blockPublisher:', error);
      return { success: false, error };
    }
  }

  /**
   * Unblock a specific project for a user
   */
  static async unblockProject(userId: string, projectId: string): Promise<{ success: boolean; error?: any }> {
    try {
      const currentPrefs = await this.getUserPreferences(userId);
      const updatedBlockedProjects = currentPrefs.blocked_projects.filter(id => id !== projectId);

      return await this.updateUserPreferences(userId, {
        blocked_projects: updatedBlockedProjects
      });
    } catch (error) {
      console.error('Error in unblockProject:', error);
      return { success: false, error };
    }
  }

  /**
   * Unblock a specific publisher for a user
   */
  static async unblockPublisher(userId: string, publisherId: string): Promise<{ success: boolean; error?: any }> {
    try {
      const currentPrefs = await this.getUserPreferences(userId);
      const updatedBlockedPublishers = currentPrefs.blocked_publishers.filter(id => id !== publisherId);

      return await this.updateUserPreferences(userId, {
        blocked_publishers: updatedBlockedPublishers
      });
    } catch (error) {
      console.error('Error in unblockPublisher:', error);
      return { success: false, error };
    }
  }

  /**
   * Get user's notification statistics
   */
  static async getUserNotificationStats(userId: string): Promise<{
    notifications_today: number;
    notifications_this_week: number;
    blocked_projects_count: number;
    blocked_publishers_count: number;
  }> {
    try {
      // Get notification counts
      const { data: todayData } = await supabase
        .from('user_notification_tracking')
        .select('id')
        .eq('user_id', userId)
        .gte('sent_at', new Date().toISOString().split('T')[0]);

      const { data: weekData } = await supabase
        .from('user_notification_tracking')
        .select('id')
        .eq('user_id', userId)
        .gte('sent_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString());

      // Get user preferences for blocked counts
      const prefs = await this.getUserPreferences(userId);

      return {
        notifications_today: todayData?.length || 0,
        notifications_this_week: weekData?.length || 0,
        blocked_projects_count: prefs.blocked_projects.length,
        blocked_publishers_count: prefs.blocked_publishers.length
      };
    } catch (error) {
      console.error('Error getting notification stats:', error);
      return {
        notifications_today: 0,
        notifications_this_week: 0,
        blocked_projects_count: 0,
        blocked_publishers_count: 0
      };
    }
  }

  /**
   * Create an unsubscribe URL for notifications
   */
  static async createUnsubscribeUrl(
    userId: string,
    projectId?: string,
    tokenType: 'project_specific' | 'all_notifications' | 'publisher_specific' = 'project_specific',
    baseUrl: string = window.location.origin
  ): Promise<string | null> {
    const { token } = await this.generateUnsubscribeToken(userId, projectId, tokenType);
    
    if (!token) {
      return null;
    }

    return `${baseUrl}/unsubscribe?token=${token}`;
  }
}
